import { GoogleOAuthProvider } from '@react-oauth/google';
import CryptoJS from 'crypto-js';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useInRouterContext } from 'react-router-dom';

// Shared
import Configs from '@/Configs';
import { persistLoginBrandCache } from '@/authentication/core/loginBrandCache.js';
import { useAuth } from '@/common/appContext';
import { useData } from '@/common/dataContext';
import { UnifiedIcon } from '@/components/UnifiedIcon';
import { KincodeButton } from '@/components/button/KincodeButton';
import { CountrySelect } from '@/components/CountrySelect/CountrySelect';
import {
  evaluatePasswordPolicy,
  meetsPasswordPolicy,
  PASSWORD_POLICY_ERROR_KEYS,
  PASSWORD_POLICY_RULE_IDS,
} from '@/utils/passwordPolicy';
import { useToastMessage } from '@/utils/useToastMessage';
import ConfettiAnimation from '@/widgets/confetti/ConfettiAnimation';

// Relative imports
import {
  getSelfOnboardingSignupRemainingMinutes,
  SelfOnboardingSignupStepper,
  type SelfOnboardingSignupStepId,
} from './SelfOnboardingSignupStepper';
import {
  SelfOnboardingWorkAccountButtons,
  type SelfOnboardingSsoDraft,
} from './SelfOnboardingWorkAccountButtons';
import { buildSelfOnboardingCountryOptions } from './core/selfOnboardingCountries';
import {
  getSelfOnboardingCampaignLanguage,
  isSelfOnboardingCampaignLanguage,
  type SelfOnboardingBrandingResult,
} from './core/apiCalls';
import {
  buildSelfOnboardingSignupUrl,
  EMAIL_REGEXP,
  SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY,
  SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY,
  SELF_ONBOARDING_EMAIL_STORAGE_KEY,
  SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY,
  SELF_ONBOARDING_TICKET_STORAGE_KEY,
  SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY,
} from './core/selfOnboardingConstants';
import {
  SELF_ONBOARDING_AMPLITUDE_EVENTS,
  trackSelfOnboarding,
} from './core/trackSelfOnboarding';
import {
  useSelfOnboardingBranding,
  useSelfOnboardingRegister,
  useSelfOnboardingResend,
  useSelfOnboardingVerify,
} from './core/useSelfOnboardingMutations';

type FlowPhase = 'signup' | 'check-email' | 'verifying' | 'branding' | 'verify-error';

type FieldErrors = {
  companyName?: boolean;
  country?: boolean;
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  password?: boolean;
};

/**
 * Locale sent to register/resend verify-email. Prefer the active campaign/i18n locale.
 */
function resolveMailLanguage(lng: string): string {
  if (isSelfOnboardingCampaignLanguage(lng)) {
    return lng;
  }
  const lower = (lng || 'en').toLowerCase();
  if (lower.startsWith('es')) return 'es';
  if (lower === 'pt-br' || lower.startsWith('pt-br')) return 'pt-BR';
  if (lower.startsWith('pt')) return 'pt-PT';
  if (lower.startsWith('fr')) return 'fr';
  if (lower.startsWith('ca')) return 'ca';
  if (lower.startsWith('de')) return 'de';
  if (lower === 'en-gb' || lower.startsWith('en-gb')) return 'en-GB';
  return 'en';
}

function readVerifyTokenFromLocation(): string | null {
  try {
    const token = new URL(window.location.href).searchParams.get('token');
    return token && token.trim() ? token.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Required signup campaign slug from `?campaign=` (kebab-case). Missing or empty → block signup.
 */
function readCampaignSlugFromLocation(): string | null {
  try {
    const raw = new URL(window.location.href).searchParams.get('campaign');
    if (!raw) {
      return null;
    }
    const slug = raw.trim().toLowerCase();
    return slug || null;
  } catch {
    return null;
  }
}

function isSelfOnboardingRegisterSuccess(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { status?: unknown }).status === 'ok'
  );
}

function goToLogin(): void {
  window.location.assign('/');
}

/**
 * Same sessionStorage keys as password login (ids + csrf). Bearer stays in React state —
 * localhost:3000→:3001 cannot rely on authCookie alone after a hard reload.
 */
function persistSelfOnboardingSessionIds(response: SelfOnboardingBrandingResult): void {
  if (response.csrfToken) {
    sessionStorage.setItem(
      'csrfToken',
      CryptoJS.AES.encrypt(response.csrfToken.toString(), Configs.privateKey).toString()
    );
  }
  sessionStorage.setItem(
    'companyID',
    CryptoJS.AES.encrypt(response.companyID.toString(), Configs.privateKey).toString()
  );
  sessionStorage.setItem(
    'employeeID',
    CryptoJS.AES.encrypt(response.employeeID.toString(), Configs.privateKey).toString()
  );
}

function readStored(key: string): string {
  try {
    return sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function rememberFounder(founderName: string, companyName: string): void {
  try {
    sessionStorage.setItem(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY, founderName);
    sessionStorage.setItem(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY, companyName);
    sessionStorage.setItem(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY, '1');
  } catch {
    /* ignore quota */
  }
}

type SignupFormState = {
  companyName: string;
  country: string | null;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const EMPTY_SIGNUP: SignupFormState = {
  companyName: '',
  country: null,
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

/**
 * Public company self-onboarding: signup → check email → verified congratulations → platform setup.
 */
export function NewCompanyOnboarding() {
  const { t, i18n } = useTranslation();
  const { errorToast, successToast } = useToastMessage();
  const inRouter = useInRouterContext();
  const {
    authenticated,
    token,
    setToken,
    setAuthenticated,
    setCompanyID,
    setEmployeeID,
    setSecurityLevel,
    setHrbpModuleAccess,
  } = useAuth();
  const { profile, setProfile, setCompanyPreferences, setCompanyBackgroundImageURL } = useData();
  const mailLanguage = resolveMailLanguage(i18n.language || 'en');
  const countryOptions = useMemo(
    () => buildSelfOnboardingCountryOptions(i18n.language || 'en'),
    [i18n.language]
  );

  const { registerCompany, loading: registerLoading } = useSelfOnboardingRegister();
  const { resendVerification, loading: resendLoading } = useSelfOnboardingResend();
  const { verifyToken, loading: verifyLoading } = useSelfOnboardingVerify();
  const { completeBranding, loading: brandingLoading } = useSelfOnboardingBranding();

  const urlToken = useMemo(() => readVerifyTokenFromLocation(), []);
  const campaignSlug = useMemo(() => readCampaignSlugFromLocation(), []);
  const [phase, setPhase] = useState<FlowPhase>(() => (urlToken ? 'verifying' : 'signup'));
  const [localeReady, setLocaleReady] = useState(() => !campaignSlug || Boolean(urlToken));
  const [signupStep, setSignupStep] = useState<SelfOnboardingSignupStepId>('company');
  const [signup, setSignup] = useState<SignupFormState>(EMPTY_SIGNUP);
  const [ssoDraft, setSsoDraft] = useState<SelfOnboardingSsoDraft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [emailTaken, setEmailTaken] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [storedEmail, setStoredEmail] = useState(() => {
    try {
      return sessionStorage.getItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });
  const [brandingTicket, setBrandingTicket] = useState(() =>
    readStored(SELF_ONBOARDING_TICKET_STORAGE_KEY)
  );
  const [founderName, setFounderName] = useState(() =>
    readStored(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY)
  );
  const [companyName, setCompanyName] = useState(() =>
    readStored(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY)
  );
  const [confettiTrigger, setConfettiTrigger] = useState(0);

  const busy = registerLoading || resendLoading || verifyLoading || brandingLoading;

  const celebrateSignupComplete = useCallback(() => {
    setConfettiTrigger((previous) => previous + 1);
  }, []);
  const passwordPolicy = useMemo(
    () => evaluatePasswordPolicy(signup.password),
    [signup.password]
  );

  useEffect(() => {
    if (urlToken || !campaignSlug) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const language = await getSelfOnboardingCampaignLanguage(campaignSlug);
        if (cancelled) return;
        if (isSelfOnboardingCampaignLanguage(language) && i18n.language !== language) {
          await i18n.changeLanguage(language);
        }
      } catch {
        /* Keep the browser locale when the campaign has no language. */
      } finally {
        if (!cancelled) setLocaleReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignSlug, i18n, urlToken]);

  useEffect(() => {
    if (!urlToken) {
      return;
    }
    let storedToken = '';
    try {
      storedToken = sessionStorage.getItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY) || '';
    } catch {
      storedToken = '';
    }
    const displayReady = readStored(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY) === '1';
    if (storedToken === urlToken && brandingTicket && displayReady) {
      setPhase('branding');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await verifyToken(urlToken);
        if (cancelled) return;
        if (isSelfOnboardingCampaignLanguage(result?.language) && i18n.language !== result.language) {
          await i18n.changeLanguage(result.language);
        }
        if (cancelled) return;
        if (result?.status === 'already_active') {
          goToLogin();
          return;
        }
        const ticket = result?.brandingTicket;
        if (typeof ticket !== 'string' || !ticket) {
          setPhase('verify-error');
          return;
        }
        try {
          sessionStorage.setItem(SELF_ONBOARDING_TICKET_STORAGE_KEY, ticket);
          sessionStorage.setItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY, urlToken);
        } catch {
          /* ignore quota */
        }
        const nextFounderName = typeof result.founderName === 'string' ? result.founderName.trim() : '';
        const nextCompanyName = typeof result.companyName === 'string' ? result.companyName.trim() : '';
        setFounderName(nextFounderName);
        setCompanyName(nextCompanyName);
        rememberFounder(nextFounderName, nextCompanyName);
        setBrandingTicket(ticket);
        trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.verifySuccess, {
          campaign: campaignSlug,
          language: i18n.language,
          authMethod: 'password',
        });
        setPhase('branding');
      } catch {
        if (!cancelled) {
          setPhase('verify-error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [brandingTicket, campaignSlug, i18n, urlToken, verifyToken]);

  useEffect(() => {
    if (phase === 'signup' && !campaignSlug) {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.campaignRequired, {
        language: i18n.language,
      });
      return;
    }
    if (phase === 'signup' && campaignSlug && localeReady && signupStep === 'company') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.signupCompanyView, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      return;
    }
    if (phase === 'signup' && campaignSlug && localeReady && signupStep === 'you') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.signupYouView, {
        campaign: campaignSlug,
        language: i18n.language,
        country: signup.country,
      });
      return;
    }
    if (phase === 'check-email') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.checkEmailView, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      return;
    }
    if (phase === 'verifying') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.verifyingView, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      return;
    }
    if (phase === 'branding') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.brandingView, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      return;
    }
    if (phase === 'verify-error') {
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.verifyError, {
        campaign: campaignSlug,
        language: i18n.language,
      });
    }
  }, [campaignSlug, i18n.language, localeReady, phase, signup.country, signupStep]);

  const handleSignupChange = useCallback((field: keyof SignupFormState, value: string | null) => {
    setSignup((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
    if (field === 'email') {
      setEmailTaken(false);
    }
    setSignupError(null);
  }, []);

  const handleSignupCompanyNext = useCallback(() => {
    const errors: FieldErrors = {
      companyName: !signup.companyName.trim(),
      country: !signup.country,
    };
    setFieldErrors(errors);
    setSignupError(null);
    if (Object.values(errors).some(Boolean)) {
      return;
    }
    setSignupStep('you');
  }, [signup.companyName, signup.country]);

  const handleSignupBack = useCallback(() => {
    trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.signupBackToCompany, {
      campaign: campaignSlug,
      language: i18n.language,
      country: signup.country,
    });
    setSignupStep('company');
    setFieldErrors({});
    setSignupError(null);
  }, [campaignSlug, i18n.language, signup.country]);

  const handleSignupSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!campaignSlug) {
        return;
      }
      if (signupStep !== 'you') {
        handleSignupCompanyNext();
        return;
      }
      const errors: FieldErrors = {
        companyName: !signup.companyName.trim(),
        country: !signup.country,
        firstName: !(ssoDraft?.firstName || signup.firstName).trim(),
        lastName: !(ssoDraft?.lastName || signup.lastName).trim(),
        email: !EMAIL_REGEXP.test((ssoDraft?.email || signup.email).trim()),
        password: ssoDraft ? false : !meetsPasswordPolicy(signup.password),
      };
      setFieldErrors(errors);
      setEmailTaken(false);
      setSignupError(null);
      if (errors.companyName || errors.country) {
        setSignupStep('company');
        return;
      }
      if (Object.values(errors).some(Boolean)) {
        return;
      }

      const email = (ssoDraft?.email || signup.email).trim().toLowerCase();
      const firstName = (ssoDraft?.firstName || signup.firstName).trim();
      const lastName = (ssoDraft?.lastName || signup.lastName).trim();
      try {
        const result = await registerCompany({
          companyName: signup.companyName.trim(),
          country: signup.country as string,
          firstName,
          lastName,
          email,
          ...(ssoDraft
            ? { signupTicket: ssoDraft.signupTicket }
            : { password: CryptoJS.HmacSHA512(signup.password, Configs.privateKey).toString() }),
          language: mailLanguage,
          campaign: campaignSlug,
        });
        if (
          result &&
          typeof result === 'object' &&
          typeof result.brandingTicket === 'string' &&
          result.brandingTicket
        ) {
          if (isSelfOnboardingCampaignLanguage(result.language) && i18n.language !== result.language) {
            await i18n.changeLanguage(result.language);
          }
          try {
            sessionStorage.setItem(SELF_ONBOARDING_TICKET_STORAGE_KEY, result.brandingTicket);
            sessionStorage.setItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY, email);
          } catch {
            /* ignore quota */
          }
          const nextFounderName = `${firstName} ${lastName}`.trim();
          const nextCompanyName = signup.companyName.trim();
          setFounderName(nextFounderName);
          setCompanyName(nextCompanyName);
          rememberFounder(nextFounderName, nextCompanyName);
          setBrandingTicket(result.brandingTicket);
          trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.registerSuccess, {
            campaign: campaignSlug,
            language: i18n.language,
            country: signup.country,
            authMethod: 'sso',
          });
          trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.verifySuccess, {
            campaign: campaignSlug,
            language: i18n.language,
            country: signup.country,
            authMethod: 'sso',
          });
          celebrateSignupComplete();
          setPhase('branding');
          return;
        }
        if (result === 409) {
          trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.registerEmailTaken, {
            campaign: campaignSlug,
            language: i18n.language,
            country: signup.country,
            authMethod: ssoDraft ? 'sso' : 'password',
          });
          setEmailTaken(true);
          return;
        }
        if (!isSelfOnboardingRegisterSuccess(result)) {
          const isClientError = typeof result === 'number' && result >= 400 && result < 500;
          trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.registerError, {
            campaign: campaignSlug,
            language: i18n.language,
            country: signup.country,
            authMethod: ssoDraft ? 'sso' : 'password',
            reason: isClientError ? 'client_error' : 'server_error',
          });
          setSignupError(
            campaignSlug && isClientError
              ? t('plg.selfOnboarding.error.campaignInvalid')
              : t('plg.selfOnboarding.error.generic')
          );
          return;
        }
        try {
          sessionStorage.setItem(SELF_ONBOARDING_EMAIL_STORAGE_KEY, email);
          sessionStorage.removeItem(SELF_ONBOARDING_TICKET_STORAGE_KEY);
          sessionStorage.removeItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.registerSuccess, {
          campaign: campaignSlug,
          language: i18n.language,
          country: signup.country,
          authMethod: ssoDraft ? 'sso' : 'password',
        });
        celebrateSignupComplete();
        setStoredEmail(email);
        setPhase('check-email');
      } catch {
        trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.registerError, {
          campaign: campaignSlug,
          language: i18n.language,
          country: signup.country,
          authMethod: ssoDraft ? 'sso' : 'password',
          reason: 'exception',
        });
        setSignupError(t('plg.selfOnboarding.error.generic'));
      }
    },
    [
      campaignSlug,
      celebrateSignupComplete,
      handleSignupCompanyNext,
      i18n,
      mailLanguage,
      registerCompany,
      signup,
      signupStep,
      ssoDraft,
      t,
    ]
  );

  const handleResend = useCallback(async () => {
    if (!storedEmail) {
      errorToast(t('plg.selfOnboarding.error.resendNeedEmail'));
      return;
    }
    try {
      await resendVerification({ email: storedEmail, language: mailLanguage });
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.resendClick, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      successToast(t('plg.selfOnboarding.checkEmail.resent'));
    } catch {
      errorToast(t('plg.selfOnboarding.error.generic'));
    }
  }, [
    campaignSlug,
    errorToast,
    i18n.language,
    mailLanguage,
    resendVerification,
    storedEmail,
    successToast,
    t,
  ]);

  const handleContinue = useCallback(async () => {
    if (!brandingTicket) {
      setPhase('verify-error');
      return;
    }
    try {
      const response = await completeBranding({
        brandingTicket,
        skip: true,
      });
      trackSelfOnboarding(SELF_ONBOARDING_AMPLITUDE_EVENTS.brandingContinue, {
        campaign: campaignSlug,
        language: i18n.language,
      });
      try {
        sessionStorage.removeItem(SELF_ONBOARDING_TICKET_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_VERIFIED_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_FOUNDER_NAME_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_COMPANY_NAME_STORAGE_KEY);
        sessionStorage.removeItem(SELF_ONBOARDING_DISPLAY_READY_STORAGE_KEY);
      } catch {
        /* ignore */
      }

      // Same contract as password login — App only mounts the shell when all three are set.
      const hasSession =
        typeof response?.bearerToken === 'string' &&
        response.bearerToken.length > 0 &&
        response?.companyID != null &&
        response?.employeeID != null &&
        response?.profile != null &&
        typeof response.profile === 'object';

      if (!hasSession) {
        errorToast(t('plg.selfOnboarding.error.generic'));
        return;
      }

      try {
        persistSelfOnboardingSessionIds(response);
      } catch {
        /* ignore quota */
      }

      setToken(response.bearerToken);
      setCompanyID(response.companyID);
      setEmployeeID(response.employeeID);
      setProfile(response.profile);
      const securityLevels = (response.profile as { roles?: { securityLevels?: unknown } })?.roles
        ?.securityLevels;
      if (securityLevels) setSecurityLevel(securityLevels);
      setHrbpModuleAccess(
        (response.profile as { hrbpModuleAccess?: string[] | null }).hrbpModuleAccess ?? null
      );
      const companyPreferences = (
        response.profile as {
          companyPreferences?: {
            personalization?: { backgroundImageURL?: string };
          };
        }
      )?.companyPreferences;
      if (companyPreferences) {
        setCompanyPreferences(companyPreferences);
        persistLoginBrandCache(
          response.companyID,
          companyPreferences as Parameters<typeof persistLoginBrandCache>[1]
        );
        const backgroundImageURL = companyPreferences?.personalization?.backgroundImageURL;
        if (backgroundImageURL) setCompanyBackgroundImageURL(backgroundImageURL);
      }

      // Must leave /customerOnboarding before App mounts authenticated Routing —
      // that path is also registered inside Routing and would re-show this screen.
      window.history.replaceState(null, '', '/');
      setAuthenticated(true);
    } catch {
      errorToast(t('plg.selfOnboarding.error.generic'));
    }
  }, [
    brandingTicket,
    campaignSlug,
    completeBranding,
    errorToast,
    i18n.language,
    setAuthenticated,
    setCompanyBackgroundImageURL,
    setCompanyID,
    setCompanyPreferences,
    setEmployeeID,
    setHrbpModuleAccess,
    setProfile,
    setSecurityLevel,
    setToken,
    t,
  ]);

  const inputClass =
    'input-field h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-slate-950 shadow-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-50 dark:focus:border-indigo-400 dark:focus:bg-slate-800';
  const labelClass =
    'mb-1.5 block w-full text-left text-sm font-semibold tracking-tight text-slate-700 dark:text-slate-200';
  const errorClass = 'mt-1 text-left text-xs text-red-600 dark:text-red-400';

  // Authenticated Routing still registers this path — bounce into the app shell.
  if (inRouter && authenticated && token && profile) {
    return <Navigate to="/" replace />;
  }

  return (
    <GoogleOAuthProvider clientId={Configs.googleClientID}>
    <div
      data-testid="plg-company-self-onboarding"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-x-hidden overflow-y-auto bg-[#020617] p-4 font-sans sm:p-8 lg:p-12"
      style={{
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(129, 140, 248, 0.2) 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl"
      />
      {confettiTrigger > 0 ? (
        <ConfettiAnimation
          key={confettiTrigger}
          level="large"
          trigger={true}
          onComplete={() => setConfettiTrigger(0)}
        />
      ) : null}
      <section
        data-testid="plg-new-company-onboarding-panel"
        className="relative flex max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col items-stretch overflow-hidden rounded-[2rem] border border-white/70 bg-white p-6 text-left text-slate-950 shadow-kcLightOverlays dark:border-slate-700 dark:bg-slate-900 dark:text-slate-50 sm:max-h-[calc(100dvh-4rem)] sm:p-8 lg:max-h-[calc(100dvh-6rem)] lg:p-10"
      >
        <div
          data-testid="plg-new-company-onboarding-header"
          className="mb-6 flex w-full shrink-0 items-center justify-between gap-4 border-b border-slate-100 pb-6 dark:border-slate-800"
        >
          <img
            className="h-8 w-auto object-contain object-center"
            alt="Kincode"
            src={Configs.companyLogoLarge}
          />
          {phase === 'signup' && campaignSlug && localeReady ? (
            <p
              className="inline-flex shrink-0 items-center gap-1.5 text-right text-xs font-medium text-slate-500 dark:text-slate-400"
              data-testid="plg-signup-time-remaining"
            >
              <UnifiedIcon name="Clock" library="phosphor" className="h-3.5 w-3.5 shrink-0" />
              {t('plg.selfOnboarding.signup.timeRemaining', {
                count: getSelfOnboardingSignupRemainingMinutes(signupStep),
              })}
            </p>
          ) : null}
        </div>

        {phase === 'verifying' ? (
          <div className="flex min-h-64 flex-col items-center justify-center text-center">
            <div className="mb-6 h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.verifying')}
            </p>
          </div>
        ) : null}

        {phase === 'signup' && !campaignSlug ? (
          <div
            data-testid="plg-signup-campaign-required"
            className="flex w-full flex-col gap-3"
          >
            <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
              {t('plg.selfOnboarding.signup.title')}
            </h1>
            <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.error.campaignRequired')}
            </p>
          </div>
        ) : null}

        {phase === 'signup' && campaignSlug && !localeReady ? (
          <div
            className="flex min-h-64 items-center justify-center"
            aria-busy="true"
            data-testid="plg-signup-locale-loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
          </div>
        ) : null}

        {phase === 'signup' && campaignSlug && localeReady ? (
          <form
            data-testid="plg-company-signup"
            className="flex min-h-0 w-full flex-1 flex-col items-stretch text-left"
            onSubmit={handleSignupSubmit}
            noValidate
          >
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain pb-2">
              <SelfOnboardingSignupStepper activeStep={signupStep} />

              <div className="mb-1 w-full text-left">
                <h1 className="font-title text-left text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
                  {signupStep === 'you'
                    ? t('plg.selfOnboarding.signup.titleYou')
                    : t('plg.selfOnboarding.signup.title')}
                </h1>
                <p className="mt-3 text-left text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {signupStep === 'you'
                    ? t('plg.selfOnboarding.signup.subtitleYou')
                    : t('plg.selfOnboarding.signup.subtitle')}
                </p>
              </div>

              {signupStep === 'company' ? (
                <>
                  <div className="w-full text-left">
                    <label className={labelClass} htmlFor="plg-signup-company-name">
                      {t('plg.selfOnboarding.fields.companyName')}
                    </label>
                    <input
                      id="plg-signup-company-name"
                      data-testid="plg-signup-company-name"
                      className={inputClass}
                      value={signup.companyName}
                      onChange={(event) => handleSignupChange('companyName', event.target.value)}
                      autoComplete="organization"
                    />
                    {fieldErrors.companyName ? (
                      <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
                    ) : null}
                  </div>

                  <div className="w-full text-left" data-testid="plg-signup-country">
                    <label className={labelClass} htmlFor="plg-signup-country-input">
                      {t('plg.selfOnboarding.fields.country')}
                    </label>
                    <CountrySelect
                      inputId="plg-signup-country-input"
                      options={countryOptions}
                      value={signup.country}
                      onChange={(code) => handleSignupChange('country', code)}
                      isDisabled={busy}
                      variant="plg"
                    />
                    {fieldErrors.country ? (
                      <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {signupStep === 'you' ? (
                <>
                  {ssoDraft ? (
                    <div
                      data-testid="plg-signup-sso-identity"
                      className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-left text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    >
                      <p>{t('plg.selfOnboarding.signup.ssoLocked', { email: ssoDraft.email })}</p>
                      <button
                        type="button"
                        className="self-start text-sm font-medium text-indigo-700 underline dark:text-indigo-300"
                        data-testid="plg-signup-sso-clear"
                        onClick={() => setSsoDraft(null)}
                      >
                        {t('plg.selfOnboarding.signup.usePassword')}
                      </button>
                    </div>
                  ) : (
                    <SelfOnboardingWorkAccountButtons
                      campaignSlug={campaignSlug}
                      disabled={busy}
                      onDraft={(draft) => {
                        setSsoDraft(draft);
                        setSignup((prev) => ({
                          ...prev,
                          firstName: draft.firstName,
                          lastName: draft.lastName,
                          email: draft.email,
                          password: '',
                        }));
                        setEmailTaken(false);
                        setSignupError(null);
                      }}
                      onMessage={setSignupError}
                    />
                  )}

                  <div className="w-full text-left">
                    <label className={labelClass} htmlFor="plg-signup-first-name">
                      {t('plg.selfOnboarding.fields.firstName')}
                    </label>
                    <input
                      id="plg-signup-first-name"
                      data-testid="plg-signup-first-name"
                      className={inputClass}
                      value={signup.firstName}
                      readOnly={Boolean(ssoDraft)}
                      onChange={(event) => handleSignupChange('firstName', event.target.value)}
                      autoComplete="given-name"
                    />
                    {fieldErrors.firstName ? (
                      <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
                    ) : null}
                  </div>

                  <div className="w-full text-left">
                    <label className={labelClass} htmlFor="plg-signup-last-name">
                      {t('plg.selfOnboarding.fields.lastName')}
                    </label>
                    <input
                      id="plg-signup-last-name"
                      data-testid="plg-signup-last-name"
                      className={inputClass}
                      value={signup.lastName}
                      readOnly={Boolean(ssoDraft)}
                      onChange={(event) => handleSignupChange('lastName', event.target.value)}
                      autoComplete="family-name"
                    />
                    {fieldErrors.lastName ? (
                      <p className={errorClass}>{t('plg.selfOnboarding.validation.required')}</p>
                    ) : null}
                  </div>

                  <div className="w-full text-left">
                    <label className={labelClass} htmlFor="plg-signup-email">
                      {t('plg.selfOnboarding.fields.email')}
                    </label>
                    <input
                      id="plg-signup-email"
                      data-testid="plg-signup-email"
                      type="email"
                      className={inputClass}
                      value={signup.email}
                      readOnly={Boolean(ssoDraft)}
                      onChange={(event) => handleSignupChange('email', event.target.value)}
                      autoComplete="email"
                    />
                    {fieldErrors.email ? (
                      <p className={errorClass}>{t('plg.selfOnboarding.validation.email')}</p>
                    ) : null}
                    {emailTaken ? (
                      <p className={errorClass} data-testid="plg-signup-email-taken">
                        {t('plg.selfOnboarding.error.emailTaken')}
                      </p>
                    ) : null}
                  </div>

                  {ssoDraft ? null : (
                    <div className="w-full text-left">
                      <label className={labelClass} htmlFor="plg-signup-password">
                        {t('plg.selfOnboarding.fields.password')}
                      </label>
                      <input
                        id="plg-signup-password"
                        data-testid="plg-signup-password"
                        type="password"
                        className={inputClass}
                        value={signup.password}
                        onChange={(event) => handleSignupChange('password', event.target.value)}
                        autoComplete="new-password"
                      />
                      {signup.password ? (
                        <ul
                          className="mt-2 space-y-1"
                          data-testid="plg-signup-password-policy"
                          aria-live="polite"
                        >
                          {PASSWORD_POLICY_RULE_IDS.map((ruleId) => {
                            const passed = passwordPolicy[ruleId];
                            return (
                              <li
                                key={ruleId}
                                className={`flex items-center gap-1.5 text-xs ${
                                  passed
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : 'text-slate-500 dark:text-slate-400'
                                }`}
                              >
                                <UnifiedIcon
                                  name={passed ? 'CheckCircle' : 'Circle'}
                                  library="phosphor"
                                  className="h-3.5 w-3.5 shrink-0"
                                />
                                {t(PASSWORD_POLICY_ERROR_KEYS[ruleId])}
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                      {fieldErrors.password ? (
                        <p className={errorClass}>{t('plg.selfOnboarding.validation.password')}</p>
                      ) : null}
                    </div>
                  )}

                  {signupError ? (
                    <p className={errorClass} data-testid="plg-signup-error" role="alert">
                      {signupError}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>

            <div
              data-testid="plg-signup-cta-bar"
              className="sticky bottom-0 z-10 -mx-6 mt-auto shrink-0 border-t border-slate-100 bg-white/95 px-6 pt-4 backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10"
            >
              {signupStep === 'company' ? (
                <KincodeButton
                  type="button"
                  data-testid="plg-signup-next"
                  variant="primary"
                  colorHex="#4F46E5"
                  className="w-full"
                  disabled={busy}
                  onClick={handleSignupCompanyNext}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {t('plg.selfOnboarding.signup.next')}
                    <UnifiedIcon name="ArrowRight" library="phosphor" className="h-4 w-4" />
                  </span>
                </KincodeButton>
              ) : (
                <div className="flex w-full flex-col gap-3 sm:flex-row-reverse">
                  <KincodeButton
                    type="submit"
                    data-testid="plg-company-signup-submit"
                    variant="primary"
                    colorHex="#4F46E5"
                    className="w-full sm:flex-1"
                    disabled={busy}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {t('plg.selfOnboarding.signup.submit')}
                      <UnifiedIcon name="ArrowRight" library="phosphor" className="h-4 w-4" />
                    </span>
                  </KincodeButton>
                  <KincodeButton
                    type="button"
                    data-testid="plg-signup-back"
                    variant="secondary"
                    colorHex="#4F46E5"
                    className="w-full sm:w-auto"
                    disabled={busy}
                    onClick={handleSignupBack}
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <UnifiedIcon name="ArrowLeft" library="phosphor" className="h-4 w-4" />
                      {t('plg.selfOnboarding.signup.back')}
                    </span>
                  </KincodeButton>
                </div>
              )}
            </div>
          </form>
        ) : null}

        {phase === 'check-email' ? (
          <div
            data-testid="plg-check-email"
            className="flex min-h-64 w-full flex-col items-center justify-center gap-5 text-center"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-2xl dark:bg-indigo-500/10">
              ✉
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700 dark:text-indigo-300">
                {t('plg.selfOnboarding.checkEmail.eyebrow')}
              </p>
              <h1 className="mt-3 font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white">
                {t('plg.selfOnboarding.checkEmail.title')}
              </h1>
            </div>
            <p className="max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
              {storedEmail
                ? t('plg.selfOnboarding.checkEmail.bodyWithEmail', { email: storedEmail })
                : t('plg.selfOnboarding.checkEmail.body')}
            </p>
            <KincodeButton
              type="button"
              data-testid="plg-check-email-resend"
              variant="secondary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              disabled={busy || !storedEmail}
              onClick={() => {
                void handleResend();
              }}
            >
              {t('plg.selfOnboarding.checkEmail.resend')}
            </KincodeButton>
          </div>
        ) : null}

        {phase === 'verify-error' ? (
          <div
            data-testid="plg-verify-error"
            className="flex min-h-64 w-full flex-col items-center justify-center gap-4 text-center"
          >
            <h1 className="font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white">
              {t('plg.selfOnboarding.verifyError.title')}
            </h1>
            <p className="max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t('plg.selfOnboarding.verifyError.body')}
            </p>
            <KincodeButton
              type="button"
              data-testid="plg-verify-error-signup"
              variant="primary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              onClick={() => {
                window.location.assign(
                  buildSelfOnboardingSignupUrl(
                    Configs.newCompanyOnboardingURL,
                    campaignSlug
                  )
                );
              }}
            >
              {t('plg.selfOnboarding.verifyError.back')}
            </KincodeButton>
          </div>
        ) : null}

        {phase === 'branding' ? (
          <div data-testid="plg-branding" className="flex w-full flex-col items-center gap-6 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-300"
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none">
                <path
                  d="M6.5 12.5 10 16l7.5-8"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                {t('plg.selfOnboarding.branding.eyebrow')}
              </p>
              <h1 className="mt-3 font-title text-3xl font-bold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-4xl">
                {founderName
                  ? t('plg.selfOnboarding.branding.titleNamed', { name: founderName })
                  : t('plg.selfOnboarding.branding.title')}
              </h1>
              <p className="mt-2 text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {t('plg.selfOnboarding.branding.thanks')}
              </p>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {companyName
                  ? t('plg.selfOnboarding.branding.bodyWithCompany', { companyName })
                  : t('plg.selfOnboarding.branding.body')}
              </p>
            </div>
            {founderName || companyName ? (
              <dl className="w-full divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 text-left dark:divide-slate-700 dark:border-slate-700 dark:bg-slate-800/60">
                {founderName ? (
                  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('plg.selfOnboarding.branding.nameLabel')}
                    </dt>
                    <dd
                      className="text-sm font-semibold text-slate-950 dark:text-white"
                      data-testid="plg-branding-founder"
                    >
                      {founderName}
                    </dd>
                  </div>
                ) : null}
                {companyName ? (
                  <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('plg.selfOnboarding.branding.companyLabel')}
                    </dt>
                    <dd
                      className="text-sm font-semibold text-slate-950 dark:text-white"
                      data-testid="plg-branding-company"
                    >
                      {companyName}
                    </dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
            <KincodeButton
              type="button"
              data-testid="plg-branding-continue"
              variant="primary"
              colorHex="#4F46E5"
              className="w-full max-w-sm"
              disabled={busy}
              onClick={() => {
                void handleContinue();
              }}
            >
              {t('plg.selfOnboarding.branding.continue')}
            </KincodeButton>
          </div>
        ) : null}
      </section>
    </div>
    </GoogleOAuthProvider>
  );
}
